import { ElementProps } from '@/components/sections/builder/types';

import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { useBuilderStore } from '@/stores/builder';
import clsx from 'clsx';
import { shallow } from 'zustand/shallow';
import { Type } from 'lucide-react';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import {
  TimelineElementDataType,
  TimelineSubtitleElementDataType,
} from '@/components/sections/builder/elements/body/timeline/types';
import InputSelect from '@/components/sections/builder/settings/input-select';

const defaultData: TimelineSubtitleElementDataType = {
  name: 'Content',
  label: '2020 - 2025',
  textStyle: 'subtitle',
  content: '',
  padding: {
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
  },
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as TimelineSubtitleElementDataType;
  const parentData = (element.parentData || {}) as TimelineElementDataType;

  return (
    <>
      <div className='relative pb-6 pl-8'>
        <div
          className='timeline-vertical-line timeline-primary-text-color absolute left-4 top-0 w-0.5 '
          style={{
            height: '100%',
            transform: 'translateX(-50%)',
            backgroundColor: parentData.colorScheme?.text || '#a855f7',
          }}
        ></div>
        <div className='timeline-content ml-4'>
          <div className='timeline-label relative mb-2'>
            <div
              className='absolute z-10 h-5 w-5 rounded-full border-2 border-white shadow-sm'
              style={{
                left: '-2rem',
                transform: 'translateX(-50%)',
                backgroundColor: parentData.colorScheme?.text || '#a855f7',
              }}
            ></div>

            <h3
              className={clsx(
                'hero-subtitle-padding  timeline-primary-text-color m-0 text-left font-bold',
                data.textStyle === 'body' && 'text-lg md:text-xl lg:text-2xl',
                data.textStyle === 'subtitle' && 'text-base md:text-lg lg:text-xl',
                data.textStyle === 'uppercase' && 'text-sm uppercase md:text-base lg:text-lg'
              )}
              data-animation='true'
              dangerouslySetInnerHTML={{ __html: data.label }}
            />
          </div>
          {data.content && (
            <div className='timeline-description '>
              <p
                className={clsx(
                  'hero-subtitle-padding timeline-secondary-text-color  m-0 text-justify leading-relaxed',
                  data.textStyle === 'body' && 'text-base md:text-lg',
                  data.textStyle === 'subtitle' && 'text-sm md:text-base',
                  data.textStyle === 'uppercase' && 'text-xs uppercase md:text-sm'
                )}
                data-animation='true'
                dangerouslySetInnerHTML={{ __html: data.content }}
              />
            </div>
          )}
        </div>
      </div>

      <style jsx>
        {`
          @media (min-width: 1024px) {
            .hero-subtitle-padding {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${data.padding?.left || 0}px;
              padding-right: ${data.padding?.right || 0}px;
            }
          }

          .timeline-primary-text-color {
            color: ${parentData.colorScheme?.text || '#6b7280'};
          }

          .timeline-secondary-text-color {
            color: ${parentData.colorScheme?.textSecondary || '#6b7280'};
          }

          .timeline-description {
            margin-top: 0.5rem;
          }

          /* Hide vertical line for last timeline item */
          .timeline-content:last-child .timeline-vertical-line {
            display: none;
          }

          /* Responsive */
          @media (max-width: 768px) {
            /* Mobile responsive styles dapat ditambahkan di sini jika diperlukan */
          }
        `}
      </style>
    </>
  );
};

export const ElementPreviewIcon = Type;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as TimelineSubtitleElementDataType;

  return (
    <div>
      <div className=''>Title/year</div>
      <TextEditorMinimal
        value={data.label}
        onChange={(val) => {
          builder.setChildData(element.parentId as string, element.id, { label: val });
        }}
        output='html'
        className='mb-2 w-full '
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
        placeholder='e.g., 2020 - 2025'
      />
      <div className=''>Description</div>
      <TextEditorMinimal
        value={data.content}
        onChange={(val) => {
          builder.setChildData(element.parentId as string, element.id, { content: val });
        }}
        output='html'
        className='mb-2 w-full '
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
        placeholder='Timeline description...'
      />
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Text Style</h4>
        <InputSelect
          defaultValue={data.textStyle}
          options={[
            { label: 'Body', value: 'body' },
            { label: 'Subtitle', value: 'subtitle' },
            { label: 'UPPERCASE', value: 'uppercase' },
          ]}
          onChange={(v) =>
            builder.setChildData(element.parentId as string, element.id, {
              textStyle: v,
            })
          }
        />
      </div>
      <PaddingSetting
        value={data.padding}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            padding: v,
          })
        }
      />
    </div>
  );
}

const TimelineContent = {
  type: 'timeline_content',
  group: 'timeline',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default TimelineContent;
